import { useState, useEffect, useCallback, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import { SPLASH_BG } from "@/constants";
import SplashScreen from "@/components/SplashScreen";
import Navbar from "@/components/Navbar";
import CustomCursor from "@/components/CustomCursor";
import HeroSection from "@/components/HeroSection";
import AboutSection from "@/components/AboutSection";
import { EmaSection, PhobosTyphoonSection, KplFcnSection } from "@/components/CompanySections";
import Footer from "@/components/Footer";
import TyphoonPage from "@/components/TyphoonPage";
import { useSiteCursor } from "@/hooks/use-site-cursor";

const SPLASH_EXIT_DELAY = 100;

const HomeContent = ({ bgRef, cursorPos, isHoveringLogo, loaded }) => (
  <div className="verum-container" data-testid="main-container">
    <div ref={bgRef} className="fixed-background" style={{ backgroundImage: `url(${SPLASH_BG})` }} />
    <CustomCursor cursorPos={cursorPos} isHoveringLogo={isHoveringLogo} />
    <Navbar />
    <HeroSection loaded={loaded} />
    <div className="parallax-spacer" />
    <AboutSection />
    <div className="parallax-spacer" />
    <EmaSection />
    <div className="parallax-spacer" />
    <PhobosTyphoonSection />
    <div className="parallax-spacer" />
    <KplFcnSection />
    <div className="parallax-spacer" />
    <Footer />
  </div>
);

const Home = () => {
  const location = useLocation();
  const skipSplash = Boolean(location.state?.skipSplash);
  const [showSplash, setShowSplash] = useState(!skipSplash);
  const [loaded, setLoaded] = useState(skipSplash);
  const bgRef = useRef(null);
  const { cursorPos, isHoveringLogo } = useSiteCursor({
    enabled: !showSplash,
    backgroundRef: bgRef,
  });

  const handleSplashComplete = useCallback(() => setShowSplash(false), [setShowSplash]);

  useEffect(() => {
    if (skipSplash) {
      setShowSplash(false);
      setLoaded(true);
      return undefined;
    }

    if (showSplash) {
      return undefined;
    }

    const timer = window.setTimeout(() => setLoaded(true), SPLASH_EXIT_DELAY);
    return () => window.clearTimeout(timer);
  }, [SPLASH_EXIT_DELAY, setLoaded, setShowSplash, showSplash, skipSplash]);

  if (showSplash) return <SplashScreen onComplete={handleSplashComplete} />;

  return <HomeContent bgRef={bgRef} cursorPos={cursorPos} isHoveringLogo={isHoveringLogo} loaded={loaded} />;
};

export default function App() {
  return (
    <BrowserRouter>
      <div className="App" data-testid="app">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/typhoon" element={<TyphoonPage />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}
